import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { clone, omit, times } from '@sama/micro-dash';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  MlExtrapolation,
  ShapeLocationState2D,
} from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import {
  ensureKeyframe,
  findInsertPoint,
  isKeyLocation,
} from '../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';

export class LocationHelper {
  constructor(private store: AbstractSensorContentStore2D) {}

  determineLocation(
    locations: ShapeLocationState2D[],
    frameNumber: number,
    insertPoint = findInsertPoint(locations, frameNumber),
    extrapolation?: MlExtrapolation<Point2D>,
  ) {
    if (isKeyLocation(locations, frameNumber, insertPoint)) {
      return omitExtrapolation(locations[insertPoint]);
    }

    const result = this.buildInitialLocation(
      locations,
      insertPoint,
      frameNumber,
      extrapolation,
    );

    if (insertPoint === 0) {
      result.visibility = 0;
      return result;
    }

    return this.determineLocationBetweenKeyFrames(
      locations,
      result,
      insertPoint,
    );
  }

  private determineLocationBetweenKeyFrames(
    locations: ShapeLocationState2D[],
    resultingLocation: ShapeLocationState2D,
    insertPoint: number,
  ) {
    const startingLocation = clone(locations[insertPoint - 1]);
    const extrapolatedLocations = startingLocation.extrapolation?.points;

    if (extrapolatedLocations) {
      const locationIndex =
        resultingLocation.frame_number - startingLocation.frame_number;

      if (locationIndex <= extrapolatedLocations.length) {
        const extrapolatedPoints = extrapolatedLocations[locationIndex - 1];
        // The visibility should be consistent with the previous keyframe
        return {
          ...resultingLocation,
          points: extrapolatedPoints,
          visibility: startingLocation.visibility,
        };
      }
      startingLocation.points =
        extrapolatedLocations[extrapolatedLocations.length - 1];
      startingLocation.frame_number += extrapolatedLocations.length;
    }

    return this.getInterpolatedLocation(
      locations,
      resultingLocation,
      startingLocation,
      insertPoint,
    );
  }

  private getInterpolatedLocation(
    locations: ShapeLocationState2D[],
    resultingLocation: ShapeLocationState2D,
    startingLocation: ShapeLocationState2D,
    insertPoint: number,
  ) {
    if (insertPoint < locations.length) {
      return this.interpolate(
        startingLocation,
        locations[insertPoint],
        resultingLocation,
      );
    }
    return { ...resultingLocation, points: startingLocation.points };
  }

  /**
   * This is what actually does the linear (inter/extra)polation.
   */
  private setFittedAttributes(
    location1: ShapeLocationState2D,
    location2: ShapeLocationState2D,
    newLocation: ShapeLocationState2D,
  ) {
    const percentDelta =
      (newLocation.frame_number - location1.frame_number) /
      (location2.frame_number - location1.frame_number);
    this.fitPoints(newLocation, location1, location2, percentDelta);
  }

  private fitPoints(
    newLocation: ShapeLocationState2D,
    location1: ShapeLocationState2D,
    location2: ShapeLocationState2D,
    percentDelta: number,
  ) {
    const len1 = location1.points.length;
    const len2 = location2.points.length;
    newLocation.points = times(Math.max(len1, len2), (i) =>
      this.fitPoint(
        location1.points[Math.min(len1 - 1, i)],
        location2.points[Math.min(len2 - 1, i)],
        percentDelta,
      ),
    );
  }

  private fitPoint(p1: Point2D, p2: Point2D, percentDelta: number) {
    return p1.map((coord, i) =>
      this.fit(coord, p2[i], percentDelta),
    ) as Point2D;
  }

  private fit(n1: number, n2: number, percentDelta: number) {
    return this.store.roundWithPixelSize((n2 - n1) * percentDelta + n1);
  }

  /**
   * This should have all attributes correct for the result _except_
   * visibility and the points that will be interpolated (i.e. overwritten in
   * determineLocationBetweenKeyFrames unless the insert point is 0). This is
   * normally controlled by the key location before `frameNumber`. If there
   * isn't one, then use the location after.
   */
  private buildInitialLocation(
    locations: ShapeLocationState2D[],
    insertPoint: number,
    frameNumber: number,
    extrapolation?: MlExtrapolation<Point2D>,
  ) {
    const location = {
      // @ts-ignore Typescript considers that we are always spreading a defined
      // object, but locations can be an empty array
      points: [],
      ...locations[Math.min(insertPoint, locations.length - 1)],
      frame_number: frameNumber,
      extrapolation,
    };

    if (!extrapolation) {
      return omitExtrapolation(location);
    }
    return location;
  }

  private interpolate(
    startingLocation: ShapeLocationState2D,
    endingLocation: ShapeLocationState2D,
    initialLocation: ShapeLocationState2D,
  ) {
    this.setFittedAttributes(startingLocation, endingLocation, initialLocation);
    initialLocation.visibility = startingLocation.visibility;
    return initialLocation;
  }
}

export function makeLocation$(
  locationHelper: LocationHelper,
  frameNumberStore$: Observable<number>,
  keyLocationStore$: Observable<ShapeLocationState2D[]>,
) {
  return combineLatest([
    frameNumberStore$,
    keyLocationStore$.pipe(filter(isDefined)),
  ]).pipe(
    map(([frameNumber, keyLocations]) =>
      locationHelper.determineLocation(keyLocations, frameNumber),
    ),
  );
}

export function ensureKeyLocation(
  locationHelper: LocationHelper,
  keyLocationStore: Store<ShapeLocationState2D[]>,
  frameNumber: number,
  specifiedLocation?: ShapeLocationState2D,
) {
  const extrapolationToCarryOver = prunePreviousKeyframe(
    keyLocationStore,
    frameNumber,
  );

  return ensureKeyframe<ShapeLocationState2D>(
    keyLocationStore,
    frameNumber,
    (insertPoint) =>
      specifiedLocation ||
      locationHelper.determineLocation(
        keyLocationStore.state(),
        frameNumber,
        insertPoint,
        extrapolationToCarryOver,
      ),
  );
}

export function omitExtrapolation(keyLocation: ShapeLocationState2D) {
  return omit(keyLocation, 'extrapolation') as ShapeLocationState2D;
}

/**
 * Removes extrapolated points from the previous keyframe and returns the
 * extrapolated points that could be carried over to a new keyframe
 */
function prunePreviousKeyframe(
  keyLocationStore: Store<ShapeLocationState2D[]>,
  currentKeyFrame: number,
) {
  const previousKeyFrameIndex =
    findInsertPoint(keyLocationStore.state(), currentKeyFrame) - 1;
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
  const previousKeyFrame = keyLocationStore.state()?.[previousKeyFrameIndex];

  if (previousKeyFrameIndex < 0 || !previousKeyFrame.extrapolation?.points) {
    return;
  }

  const extrapolatedPoints = [...previousKeyFrame.extrapolation.points];

  const prunedExtrapolatedPoints = extrapolatedPoints.splice(
    0,
    currentKeyFrame - previousKeyFrame.frame_number - 1,
  );

  updateExtrapolations(
    prunedExtrapolatedPoints,
    keyLocationStore(previousKeyFrameIndex)('extrapolation'),
  );

  const extrapolatedPointsToCarryOver = extrapolatedPoints.slice(1);
  if (!extrapolatedPointsToCarryOver.length) {
    return undefined;
  }
  return {
    source: previousKeyFrame.extrapolation.source,
    points: extrapolatedPointsToCarryOver,
  };
}

function updateExtrapolations(
  prunedExtrapolatedPoints: Point2D[][],
  extrapolationStore: Store<MlExtrapolation<Point2D> | undefined>,
) {
  if (prunedExtrapolatedPoints.length) {
    extrapolationStore.assign({ points: prunedExtrapolatedPoints });
  } else {
    extrapolationStore.delete();
  }
}
